import { useMemo, useState } from "react";
import MenuTreeProductList from "./MenuTreeProductList";
import { folderIcon } from "./menuTreeHandles";
import { productTitle, subcategoryTitle } from "./menuTreeUtils";

/**
 * One subcategory inside a category: folder row + its products.
 */
const MenuTreeSubcategoryNode = ({
  sub,
  dragHandle,
  onAdd,
  onRemove,
  onRemoveProduct,
  onToggleHideTitle,
  togglingHide,
  onReorderProducts,
  t,
}) => {
  const [expanded, setExpanded] = useState(true);
  const subId = String(sub?._id || sub?.id);
  const title = subcategoryTitle(sub);
  const hidden = sub?.hideTitleFromClient === true;

  /** Only products visible in this menu; hidden ones come back through "+ Add". */
  const products = useMemo(
    () =>
      (sub?.productList || [])
        .filter((p) => p?.product != null && p.hiddenInMenu !== true)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((p) => ({ id: String(p.product._id), title: productTitle(p.product) })),
    [sub]
  );

  return (
    <div className="menu-tree__node menu-tree__node--sub">
      <div className="menu-tree__row">
        <button
          type="button"
          className="btn menu-tree__caret"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? t("menuTreeCollapse") : t("menuTreeExpand")}
        >
          <i className={`fas ${expanded ? "fa-caret-down" : "fa-caret-right"}`} />
        </button>

        {dragHandle}
        {folderIcon("menu-tree__folder--sub")}

        <span
          className={`menu-tree__label menu-tree__label--sub ${hidden ? "menu-tree__label--muted" : ""}`}
          onClick={() => setExpanded((v) => !v)}
          title={title}
        >
          {title}
          {hidden && (
            <span className="menu-tree__hidden-badge">
              <i className="fas fa-eye-slash" aria-hidden />
              {t("hideTitleOnClientApp")}
            </span>
          )}
        </span>

        <button
          type="button"
          className="btn menu-tree__remove menu-tree__remove--row"
          title={t("menuTreeRemoveFromCategory")}
          aria-label={t("menuTreeRemoveFromCategory")}
          onClick={() => onRemove(sub)}
        >
          <i className="fas fa-times" />
        </button>

        <span className="menu-tree__count" title={t("menuTreeProductsCount", { count: products.length })}>
          {products.length}
        </span>

        <button
          type="button"
          className={`btn menu-tree__icon-btn ${hidden ? "menu-tree__icon-btn--active" : ""}`}
          title={t("hideSubcategoryTitleFromClientHelp")}
          aria-pressed={hidden}
          disabled={togglingHide}
          onClick={() => onToggleHideTitle(subId, !hidden)}
        >
          <i className={`fas ${hidden ? "fa-eye-slash" : "fa-eye"}`} />
        </button>

        <button type="button" className="btn menu-tree__add" onClick={() => onAdd(sub)}>
          + <span>{t("menuTreeAddShort")}</span>
        </button>
      </div>

      {expanded && (
        <div className="menu-tree__children menu-tree__children--leaf">
          {products.length === 0 ? (
            <div className="menu-tree__empty">
              {t("noProductsInSubcategory")}
              <button type="button" className="btn btn-link" onClick={() => onAdd(sub)}>
                {t("menuTreeAddProducts")}
              </button>
            </div>
          ) : (
            <MenuTreeProductList
              items={products}
              dragHint={t("menuTreeDragHint")}
              removeHint={t("menuTreeHideFromMenu")}
              onReorder={(orderedIds) => onReorderProducts(subId, orderedIds)}
              onRemove={(item) => onRemoveProduct(sub, item)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MenuTreeSubcategoryNode;
